// Sentry must initialize before React renders so Error Boundary captures any
// error in the tree, including initial-render throws.
import * as Sentry from "@sentry/react";
import {
  getSentryReactRootOptions,
  initSentry,
  SentryErrorBoundary,
} from "./sentry.js";
import { initDatadogRum } from "./datadogRum.js";
import { themeStore } from "./lib/theme.js";
initSentry();
// Datadog RUM (page performance + backend trace correlation). Dormant unless
// the build carries VITE_DD_RUM_APPLICATION_ID / VITE_DD_RUM_CLIENT_TOKEN.
initDatadogRum();
// Stamp the colour scheme on <html> before the first paint so a dark-scheme
// user never sees the light field flash (ADR-0045).
themeStore.start();

import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";

// Wrap the router's <Routes> so react-router v7 navigation transactions
// are reported to Sentry.
const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);
import { App } from "./App.js";
import { AuthPage } from "./pages/AuthPage.js";
import { McpAuthorizePage } from "./pages/McpAuthorizePage.js";
import { OAuthPopupComplete } from "./pages/OAuthPopupComplete.js";
import { InvitePage } from "./pages/InvitePage.js";
import { SlackLinkPage } from "./pages/SlackLinkPage.js";
import { ActivationOptOutPage } from "./pages/ActivationOptOutPage.js";
import { NudgesOptOutPage } from "./pages/NudgesOptOutPage.js";
import { AdminView } from "./pages/AdminView.js";
import { SystemsRoute } from "./pages/admin/SystemsPage.js";
import { StreamRoute } from "./pages/admin/StreamPage.js";
import { WorkspacesPage } from "./pages/WorkspacesPage.js";
import { AdminShell } from "./shell/AdminShell.js";
import { RunDetailPage } from "./pages/runs/RunDetailPage.js";
import { AUTHENTICATED_DEFAULT_PATH } from "./routes/defaults.js";
import { WorkAddRedirect, WorkIndexRedirect, WorkItemRedirect } from "./work/WorkRedirects.js";
import { DecisionsRoute } from "./ledger/DecisionsRoute.js";
import { ReviewsRoute } from "./reviews/ReviewsPage.js";
import { decisionUrl } from "@yagni/shared";
import { AllTeamsRoute } from "./teams/AllTeamsRoute.js";
import { WaitingRoute } from "./work/WaitingRoute.js";
import { TeamQueueRoute } from "./teams/TeamQueueRoute.js";
import { LinePageRedirect } from "./teams/LinePageRedirect.js";
import { PrototypeRoute } from "./prototypes/PrototypeRoute.js";
import { SettingsRoute } from "./settings/SettingsRoute.js";
import { UsageRoute } from "./usage/UsageReportView.js";
import { AgentUsageRoute } from "./usage/AgentUsageView.js";
import { TeamUsageRoute } from "./usage/TeamUsageView.js";
import { ConnectionsPage } from "./connections/ConnectionsPage.js";
import { CreateTeamRoute } from "./desks/CreateTeamRoute.js";
import { DoorsRoute } from "./onboarding/DoorsRoute.js";
import "./index.css";
import "./analytics.js";

/**
 * Preserve `?workItem=<id>&comment=<id>` etc. across a redirect. Bell
 * notifications and citations minted before a surface moved still carry the
 * old path; stripping the query string would drop the deep-link target.
 */
function RedirectPreservingSearch({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

/** `/knowledge/decisions/:id` (the ledger's old home under Library) → `/decisions/:id`. */
function RedirectToDecision() {
  const { decisionId } = useParams<{ decisionId: string }>();
  const { search } = useLocation();
  return <Navigate to={`${decisionUrl(decisionId ?? "")}${search}`} replace />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/*
 * The route table for the new shell (Oct 2026): the rail, the center, the
 * sheet and the Ask dock (shell/AppShell.tsx).
 *
 * Signed-in surfaces: Waiting on you (home), All teams, a run's detail,
 * Decisions, Teams (create, the Team queue, a line's page), Usage,
 * Connections, Settings, Admin. Vision and the
 * Agents record are gone and their old addresses land on Waiting on you. The
 * decision ledger's old addresses under Library go to Decisions, and
 * everything else falls through to the home.
 */
createRoot(
  document.getElementById("root")!,
  getSentryReactRootOptions(),
).render(
  <StrictMode>
    <SentryErrorBoundary>
    <BrowserRouter>
      <ScrollToTop />
      <SentryRoutes>
        {/* Marketing pages are served by Astro at /, /product, /pricing, etc.
            Only auth-related SPA routes remain here. */}
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/mcp/authorize" element={<McpAuthorizePage />} />
        <Route path="/invite/:token" element={<InvitePage />} />
        {/* Slack link-identity landing: reached via the link-token URL the
            session dispatcher posts to unlinked Slack users. Self-handles
            auth so it lives outside the App shell. */}
        <Route path="/slack/link" element={<SlackLinkPage />} />
        {/* Activation drip opt-out: the unsubscribe link in drip emails and
            Slack DMs. Public, token-signed. */}
        <Route path="/activation/opt-out" element={<ActivationOptOutPage />} />
        {/* "Stop these messages" under Wright stop DMs and the decisions
            digest sent by Slack or email. Public, token-signed. */}
        <Route path="/nudges/opt-out" element={<NudgesOptOutPage />} />
        <Route path="/share/:token" element={<Navigate to="/auth" replace />} />
        <Route path="/workspaces" element={<WorkspacesPage />} />
        {/* OAuth popup landing: posts `yagni:oauth:complete` to its opener
            and self-closes. No app chrome, no auth gate. */}
        <Route path="/oauth/popup-complete" element={<OAuthPopupComplete />} />
        <Route path="/" element={<App />}>
          <Route index element={<Navigate to={AUTHENTICATED_DEFAULT_PATH} replace />} />
          {/* The two doors: Connect GitHub, or install YAGNI Code. */}
          <Route path="welcome" element={<DoorsRoute />} />
          {/* Waiting on you is home: every ask addressed to the reader. */}
          <Route path="waiting" element={<WaitingRoute />} />
          {/* All teams: one row per Team. */}
          <Route path="teams" element={<AllTeamsRoute />} />
          {/* Work moved onto the Team queues: its old addresses (a ticket, its
              receipt, Add tickets, the index) land on the ticket's Team with
              the sheet open, or on Waiting on you. */}
          <Route path="work" element={<WorkIndexRedirect />} />
          <Route path="work/add" element={<WorkAddRedirect />} />
          <Route path="work/:workItemId" element={<WorkItemRedirect />} />
          <Route path="work/:workItemId/receipt" element={<WorkItemRedirect />} />
          <Route path="runs/:workItemId/:attemptNo" element={<RunDetailPage />} />
          {/* A prototype is design evidence for one ticket: reached from the
              case file's Design section, chat cards and prototypeUrl links. */}
          <Route path="prototypes/:prototypeId" element={<PrototypeRoute />} />
          {/* Pull request reviews: every pull request Proctor watches, as a queue. */}
          <Route path="reviews" element={<ReviewsRoute />} />
          <Route path="reviews/:prStateId" element={<ReviewsRoute />} />
          {/* Decisions: the ledger as a first-class destination. */}
          <Route path="decisions" element={<DecisionsRoute />} />
          <Route path="decisions/:decisionId" element={<DecisionsRoute />} />
          <Route path="knowledge/decisions" element={<RedirectPreservingSearch to="/decisions" />} />
          <Route path="knowledge/decisions/:decisionId" element={<RedirectToDecision />} />
          {/* Retired: the Agents record and Vision. */}
          <Route path="agents" element={<Navigate to={AUTHENTICATED_DEFAULT_PATH} replace />} />
          <Route path="agents/:slug" element={<Navigate to={AUTHENTICATED_DEFAULT_PATH} replace />} />
          <Route path="vision" element={<Navigate to={AUTHENTICATED_DEFAULT_PATH} replace />} />
          {/* Teams: Add a team (the sheet, wide, two sizes), the Team queue (the
              home of its work, a ticket open as `?item=`, its settings as `?settings=`); a
              retired line page redirects to its Agent in settings. */}
          <Route path="desks/new" element={<CreateTeamRoute />} />
          <Route path="desks/:deskId" element={<TeamQueueRoute />} />
          <Route path="desks/:deskId/lines/:jobId" element={<LinePageRedirect />} />
          <Route path="usage" element={<UsageRoute />} />
          {/* The report's drills: one Agent's runs, one Team's Agents. */}
          <Route path="usage/agents/:agentId" element={<AgentUsageRoute />} />
          <Route path="usage/teams/:deskId" element={<TeamUsageRoute />} />
          {/* Connections: the connectors, MCP servers, webhooks, and the
              repository environments. */}
          <Route path="services" element={<ConnectionsPage />} />
          <Route path="services/:tab" element={<ConnectionsPage />} />
          {/* Settings, one page with the sections as chips: Workspace, People,
              Spending, Agent runs, You. A section address opens its card; the
              retired slugs (team, sso, billing, tokens) redirect. */}
          <Route path="settings" element={<SettingsRoute />} />
          <Route path="settings/:section" element={<SettingsRoute />} />
          {/* Super admin: one shell and nav (AdminShell). `/admin` lands on
              Systems; Stream is the live feed; AdminView picks the rest off
              the path and redirects the legacy addresses. */}
          <Route path="admin" element={<AdminShell />}>
            <Route index element={<AdminView />} />
            <Route path="systems" element={<SystemsRoute />} />
            <Route path="stream" element={<StreamRoute />} />
            <Route path="overview" element={<AdminView />} />
            <Route path="spend" element={<AdminView />} />
            <Route path="enterprise-billing" element={<AdminView />} />
            <Route path="workspaces" element={<AdminView />} />
            <Route path="workspaces/:workspaceId" element={<AdminView />} />
            <Route path="workspaces/:workspaceId/:tab" element={<AdminView />} />
            <Route path="feature-flags" element={<AdminView />} />
            <Route path="model-routing" element={<AdminView />} />
            <Route path="platform" element={<AdminView />} />
            <Route path="platform/:tab" element={<AdminView />} />
            <Route path="inbox" element={<AdminView />} />
            <Route path="inbox/:tab" element={<AdminView />} />
            <Route path="inbox/feedback/:id" element={<AdminView />} />
            <Route path="prospects" element={<AdminView />} />
            {/* Legacy index entries: AdminView redirects each to its new home. */}
            <Route path="runtime" element={<AdminView />} />
            <Route path="usage" element={<AdminView />} />
            <Route path="credits" element={<AdminView />} />
            <Route path="billing" element={<AdminView />} />
            <Route path="billing/:workspaceId" element={<AdminView />} />
            <Route path="model-pins" element={<AdminView />} />
            <Route path="desktop-releases" element={<AdminView />} />
            <Route path="system" element={<AdminView />} />
            <Route path="builder" element={<AdminView />} />
            <Route path="feedback" element={<AdminView />} />
            <Route path="feedback/:id" element={<AdminView />} />
            <Route path="deletion-requests" element={<AdminView />} />
          </Route>
          {/* Everything else, including /front, lands on Waiting on you. */}
          <Route path="*" element={<Navigate to={AUTHENTICATED_DEFAULT_PATH} replace />} />
        </Route>
      </SentryRoutes>
    </BrowserRouter>
    </SentryErrorBoundary>
  </StrictMode>
);
